import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';

const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES;
const apiUrlEmpresas = `${process.env.REACT_APP_API_URL}/empresas`;

const EditAddres = () => {
    const { id_direccion } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        nombre_sede: '',
        direccion: '',
        localidad: '',
        id_empresa: ''
    });
    const [empresas, setEmpresas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, [id_direccion]);

    const loadData = async () => {
        try {
            // Cargar empresas y dirección en paralelo
            const [empresasRes, direccionRes] = await Promise.all([
                axios.get(apiUrlEmpresas),
                axios.get(`${apiUrlDirecciones}${id_direccion}`)
            ]);

            setEmpresas(empresasRes.data || []);
            const direccionData = direccionRes.data;
            setFormData({
                nombre_sede: direccionData.nombre_sede || '',
                direccion: direccionData.direccion || '',
                localidad: direccionData.localidad || '',
                id_empresa: direccionData.id_empresa || ''
            });
        } catch (error) {
            console.error('Error al cargar datos:', error);
            alert('❌ Error al cargar la sede');
            navigate('/admin/AbmDirecciones');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!formData.nombre_sede || formData.nombre_sede.trim() === '') {
            alert('⚠️ El nombre de la sede es obligatorio');
            return;
        }

        if (!formData.id_empresa) {
            alert('⚠️ Debe seleccionar una empresa');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.put(`${apiUrlDirecciones}${id_direccion}`, formData);
            alert('✅ Sede actualizada exitosamente');
            navigate('/admin/AbmDirecciones');
        } catch (error) {
            console.error('Error al actualizar sede:', error);
            alert('❌ Error al actualizar: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/AbmDirecciones');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                    <div className="flex items-center gap-4">
                        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-lg font-semibold text-gray-700">Cargando datos de la sede...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-100 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-xl p-6 border-b-4 border-cyan-500">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-4 rounded-xl shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Editar Sede</h1>
                            <p className="text-gray-600 mt-1">Modifica los datos de <span className="font-semibold text-cyan-600">{formData.nombre_sede}</span></p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleUpdate} className="bg-white rounded-b-2xl shadow-xl p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Empresa */}
                        <div className="md:col-span-2">
                            <label htmlFor="id_empresa" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-purple-600">🏢</span>
                                Empresa
                                <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="id_empresa"
                                name="id_empresa"
                                value={formData.id_empresa}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none text-gray-800"
                            >
                                <option value="">Seleccione una empresa</option>
                                {empresas.map(emp => (
                                    <option key={emp.id_empresa} value={emp.id_empresa}>
                                        {emp.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Nombre Sede */}
                        <div className="md:col-span-2">
                            <label htmlFor="nombre_sede" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-blue-600">📍</span>
                                Nombre de la Sede
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="nombre_sede"
                                name="nombre_sede"
                                value={formData.nombre_sede}
                                onChange={handleChange}
                                placeholder="Ej: Sede Central, Sucursal Norte"
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Dirección */}
                        <div className="md:col-span-2">
                            <label htmlFor="direccion" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-green-600">🏠</span>
                                Dirección
                            </label>
                            <input
                                type="text"
                                id="direccion"
                                name="direccion"
                                value={formData.direccion}
                                onChange={handleChange}
                                placeholder="Calle, número, piso, depto"
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Localidad */}
                        <div className="md:col-span-2">
                            <label htmlFor="localidad" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-cyan-600">🌆</span>
                                Localidad
                            </label>
                            <input
                                type="text"
                                id="localidad"
                                name="localidad"
                                value={formData.localidad}
                                onChange={handleChange}
                                placeholder="Ciudad, provincia"
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-cyan-700 hover:to-blue-700 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Guardar Cambios
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </span>
                        </button>
                    </div>
                </form>

                {/* Info adicional */}
                <div className="mt-6 bg-cyan-50 border-l-4 border-cyan-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-cyan-900">Ten en cuenta</p>
                            <p className="text-sm text-cyan-700 mt-1">
                                Cambiar la empresa de una sede puede afectar los reportes y filtros. Los sectores y usuarios asociados a esta sede se mantendrán vinculados.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAddres;
